import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function BookACallPage() {
  useEffect(() => {
    // Update meta tags for Book a Call page
    document.title = 'Book a Strategy Call - EasyMarketPro AI Support Consultation';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Schedule a 30-minute strategy call with EasyMarketPro to discuss how AI customer support agents can reduce your ticket volume by up to 80% and cut support costs. Get a custom volume reduction projection.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'book AI support consultation, schedule customer support call, AI agents demo, reduce ticket volume consultation, support cost reduction meeting');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://easymarketpro.tech/book-a-call');
    }
    
    // Add structured data for Service page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Customer Support Consultation",
      "description": "Schedule a strategy call to discuss how AI agents can reduce your support ticket volume",
      "provider": {
        "@type": "Organization",
        "name": "EasyMarketPro",
        "url": "https://easymarketpro.tech"
      },
      "url": "https://easymarketpro.tech/book-a-call"
    });
    document.head.appendChild(script);
    
    // Load Calendly widget script
    const calendlyScript = document.createElement('script');
    calendlyScript.src = 'https://assets.calendly.com/assets/external/widget.js';
    calendlyScript.async = true;
    document.body.appendChild(calendlyScript);

    return () => {
      document.head.removeChild(script);
      if (calendlyScript.parentNode) {
        calendlyScript.parentNode.removeChild(calendlyScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <nav className="bg-black">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/39cbf484d561e1f62e3c6532cd56d730/5b0e55db21655eca8e03e44a17fcce4c.png"
              alt="EasyMarketPro - AI Customer Support Agents"
              className="h-10 md:h-14 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-[14px] text-[#E5E5E5] hover:text-[#B8985F] transition-colors cursor-pointer font-medium whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B8985F]/10 mb-5">
            <div className="w-2 h-2 rounded-full bg-[#B8985F] animate-pulse"></div>
            <span className="text-[12px] tracking-[1.5px] uppercase text-[#B8985F] font-semibold">
              Schedule a Meeting
            </span>
          </div>
          <h1 className="text-[28px] md:text-[40px] font-bold text-[#1A1A1A] mb-3 md:mb-4">
            Book a Strategy Call
          </h1>
          <p className="text-[14px] md:text-[16px] text-[#777] max-w-[520px] mx-auto leading-[1.7]">
            Pick a time that works for you. We&apos;ll discuss how <strong>AI agents can reduce your support ticket volume</strong> and cost.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-[#F0EDE6] shadow-[0_4px_24px_rgba(0,0,0,0.04)] overflow-hidden">
          <div
            className="calendly-inline-widget"
            data-url="https://calendly.com/easymarketpro/30min"
            style={{ minWidth: '320px', height: '700px' }}
          ></div>
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-[800px] mx-auto">
          {[
            { icon: 'ri-time-line', title: '30 Minutes', text: 'Quick, focused strategy session' },
            { icon: 'ri-shield-check-line', title: 'No Commitment', text: 'Explore if AI agents are right for you' },
            { icon: 'ri-bar-chart-box-line', title: 'Custom Estimate', text: 'Get a volume reduction projection' },
          ].map((item, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center p-5 rounded-lg bg-white border border-[#F0EDE6]"
            >
              <div className="w-10 h-10 rounded-full bg-[#B8985F]/10 flex items-center justify-center mb-3">
                <i className={`${item.icon} text-[#B8985F] text-lg`}></i>
              </div>
              <p className="text-[14px] font-semibold text-[#1A1A1A] mb-1">{item.title}</p>
              <p className="text-[13px] text-[#888]">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <footer className="bg-[#F9F8F6] py-6 border-t border-[#E8E4DC]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#999] text-[13px]">&copy; 2025 EasyMarketPro. All rights reserved.</p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-[#bbb] text-[12px] hover:text-[#B8985F] transition-colors cursor-pointer whitespace-nowrap"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
